import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import * as XLSX from "xlsx";

import { db, functions as firebaseFunctions } from "../../../firebase";
import {
  CURATION_MAX_ITEMS,
  MARKET_CACHE_KEY,
  MARKET_CACHE_TTL_MS,
  MARKET_FETCH_DELAY_MS,
  MARKET_MAX_NETWORK_CALLS,
  MARKET_MAX_REFERENCE_PRODUCTS,
  MARKET_RATE_LIMIT_COOLDOWN_MS,
  MARKET_RATE_LIMIT_KEY_PREFIX,
  MARKET_RETRY_BASE_DELAY_MS,
  MARKET_RETRY_COUNT,
  MARKET_SOURCE_CONFIG,
  MARKET_SOURCE_ORDER,
} from "./constants";
import {
  buildFinalConsensusPrompt,
  buildGeminiRevisionPrompt,
  buildOpenAIDraftPrompt,
} from "./prompts";
import {
  CoupangFetchResult,
  CoupangLoadResult,
  CoupangReference,
  DatasetSummary,
  DebateResult,
  HighMarginProductSummary,
  LogLevel,
  MarketSource,
  ProductSummary,
  SalesRow,
  SupplementalCurationData,
  SupplementalProductSignal,
  WarehouseStockSummary,
} from "./types";
import {
  buildMarketProxyUrl,
  buildMarketSearchUrl,
  computeTargetItemCount,
  extractCoupangPrices,
  extractJson,
  inferCategory,
  median,
  money,
  normalizeFinalConsensus,
  normalizeProductKey,
  normalizeProposal,
  parseTargetItemCountFromRequirement,
  sleep,
  toNum,
} from "./utils";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type CoupangCacheItem = {
  timestamp: number;
  reference: CoupangReference;
};

type CoupangCacheMap = Record<string, CoupangCacheItem>;
type WarehouseInventorySummaryInput = {
  products: WarehouseStockSummary[];
  itemCount: number;
  totalStock: number;
};
type SalesRange = [Dayjs, Dayjs] | null;
type SheetSpec = {
  key: "inbound" | "outbound" | "request" | "markup";
  label: string;
  gid: number;
  expectedHeaders: string[];
};
type SheetLoadResult = {
  spec: SheetSpec;
  rows: Record<string, any>[];
  error: Error | null;
};
type ProductSignalAccumulator = {
  product_name: string;
  inbound_quantity: number;
  outbound_quantity: number;
  outbound_customers: Set<string>;
  request_count: number;
  request_companies: Set<string>;
  markup_percent: number | null;
  cost_price: number | null;
  sale_price: number | null;
  coupang_lowest_price: number | null;
};

const CURATION_SHEET_ID = "1_m__RfGqhl2Ekr3cPNDIT_jyc5zVL55SrjRQKo5Xjjg";
const CURATION_SHEET_SPECS: SheetSpec[] = [
  {
    key: "inbound",
    label: "입고",
    gid: 0,
    expectedHeaders: ["품명", "입고수량", "도착예정일"],
  },
  {
    key: "outbound",
    label: "출고",
    gid: 164243140,
    expectedHeaders: ["고객사명", "상품명", "출고수량"],
  },
  {
    key: "request",
    label: "간식신청서",
    gid: 682815548,
    expectedHeaders: ["Submission Date", "회사명", "희망하는 간식"],
  },
  {
    key: "markup",
    label: "마진",
    gid: 1694405821,
    expectedHeaders: ["품명", "판매가", "MARKUP"],
  },
];
const EMPTY_SUPPLEMENTAL_DATA: SupplementalCurationData = {
  inbound_rows: 0,
  outbound_rows: 0,
  request_rows: 0,
  request_matched_items: 0,
  markup_rows: 0,
  remaining_stock_items: 0,
  remaining_stock_total_qty: 0,
  requested_keywords: [],
  high_margin_products: [],
  products: [],
  failed_sources: [],
};

const buildSheetCsvUrl = (gid: number) =>
  `https://docs.google.com/spreadsheets/d/${CURATION_SHEET_ID}/gviz/tq?tqx=out:csv&gid=${gid}`;

const normalizeHeader = (value: any) =>
  String(value || "")
    .replace(/\uFEFF/g, "")
    .replace(/\s+/g, "")
    .trim();

const parseLooseNumber = (value: any): number | null => {
  const cleaned = String(value ?? "")
    .replace(/,/g, "")
    .replace(/%/g, "")
    .trim();
  if (!cleaned || cleaned === "#N/A" || cleaned === "N/A") return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCompanyName = (value: any) =>
  String(value || "")
    .replace(/\[[^\]]+\]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeLooseProductKey = (name: string) =>
  normalizeProductKey(name).replace(/[^0-9a-z가-힣]/gi, "");

const matrixToObjects = (matrix: any[][], headerRowIndex: number) => {
  const rawHeaders = matrix[headerRowIndex] || [];
  const headers = rawHeaders.map((cell) => String(cell || "").trim());
  return matrix
    .slice(headerRowIndex + 1)
    .map((row) =>
      headers.reduce<Record<string, any>>((acc, header, index) => {
        if (!header) return acc;
        acc[header] = row[index];
        return acc;
      }, {}),
    )
    .filter((row) => Object.values(row).some((value) => String(value || "").trim()));
};

const findHeaderRowIndex = (matrix: any[][], expectedHeaders: string[]) => {
  const normalizedExpected = expectedHeaders.map(normalizeHeader);
  for (let i = 0; i < Math.min(matrix.length, 8); i += 1) {
    const headers = (matrix[i] || []).map(normalizeHeader);
    const isMatch = normalizedExpected.every((expected) => headers.includes(expected));
    if (isMatch) return i;
  }
  return -1;
};

const fetchSheetRows = async (spec: SheetSpec): Promise<Record<string, any>[]> => {
  const response = await fetch(buildSheetCsvUrl(spec.gid));
  if (!response.ok) {
    throw new Error(`${spec.label} 시트 로딩 실패 (${response.status})`);
  }

  const csvText = await response.text();
  const workbook = XLSX.read(csvText, { type: "string" });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const matrix = XLSX.utils.sheet_to_json<any[]>(worksheet, {
    header: 1,
    defval: "",
    raw: false,
  });

  const headerRowIndex = findHeaderRowIndex(matrix, spec.expectedHeaders);
  if (headerRowIndex < 0) {
    throw new Error(`${spec.label} 시트 헤더를 찾지 못했습니다.`);
  }

  return matrixToObjects(matrix, headerRowIndex);
};

const resolveCanonicalProductName = (
  rawName: string,
  catalog: Array<{ key: string; name: string }>,
  exactMap: Map<string, string>,
) => {
  const fallback = String(rawName || "").trim();
  const key = normalizeLooseProductKey(fallback);
  if (!key) return fallback;

  const exact = exactMap.get(key);
  if (exact) return exact;

  let bestName = fallback;
  let bestScore = Number.POSITIVE_INFINITY;

  catalog.forEach((candidate) => {
    if (!candidate.key) return;
    if (!candidate.key.includes(key) && !key.includes(candidate.key)) return;
    const score = Math.abs(candidate.key.length - key.length);
    if (score < bestScore) {
      bestScore = score;
      bestName = candidate.name;
    }
  });

  return bestName;
};

const splitRequestedItems = (value: any) =>
  String(value || "")
    .split(/\r?\n|,/)
    .map((item) =>
      item
        .replace(/^\s*\d+\s*[.)-]?\s*/g, "")
        .replace(/["']/g, "")
        .trim(),
    )
    .filter(Boolean);

const buildProductPriorityScore = ({
  totalQuantity,
  totalSales,
  inboundQuantity,
  outboundQuantity,
  remainingStockQuantity,
  outboundCustomerCount,
  requestCount,
  requestCompanyCount,
  markupPercent,
}: {
  totalQuantity: number;
  totalSales: number;
  inboundQuantity: number;
  outboundQuantity: number;
  remainingStockQuantity: number;
  outboundCustomerCount: number;
  requestCount: number;
  requestCompanyCount: number;
  markupPercent: number | null;
}) =>
  Math.round(
    (Math.log10(totalQuantity + 1) * 32 +
      Math.log10(totalSales + 1) * 12 +
      Math.log10(remainingStockQuantity + 1) * 18 +
      Math.log10(inboundQuantity + 1) * 6 +
      Math.log10(outboundQuantity + 1) * 18 +
      outboundCustomerCount * 6 +
      requestCount * 10 +
      requestCompanyCount * 8 +
      Math.max(0, markupPercent ?? 0)) *
      10,
  ) / 10;

const isFreshCache = (timestamp: number) =>
  Date.now() - timestamp <= MARKET_CACHE_TTL_MS;

const readCoupangCache = (
  opts: { includeExpired?: boolean } = {},
): CoupangCacheMap => {
  const { includeExpired = false } = opts;
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(MARKET_CACHE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as Record<string, any>;
    if (!parsed || typeof parsed !== "object") return {};

    const now = Date.now();
    const cache: CoupangCacheMap = {};
    Object.entries(parsed).forEach(([key, value]) => {
      const timestamp = Number(value?.timestamp);
      const reference = value?.reference as CoupangReference | undefined;
      if (!Number.isFinite(timestamp) || !reference?.product_name) return;
      if (!includeExpired && now - timestamp > MARKET_CACHE_TTL_MS) return;
      cache[key] = { timestamp, reference };
    });
    return cache;
  } catch (_) {
    return {};
  }
};

const writeCoupangCache = (cache: CoupangCacheMap) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MARKET_CACHE_KEY, JSON.stringify(cache));
  } catch (_) {}
};

const getCachedCoupangReference = (
  source: MarketSource,
  keyword: string,
  opts: { allowStale?: boolean } = {},
): { reference: CoupangReference | null; stale: boolean } => {
  const { allowStale = false } = opts;
  const cache = readCoupangCache({ includeExpired: allowStale });
  const key = `${source}::${normalizeProductKey(keyword)}`;
  const item = cache[key];
  if (!item?.reference) {
    return { reference: null, stale: false };
  }
  return {
    reference: item.reference,
    stale: !isFreshCache(item.timestamp),
  };
};

const setCachedCoupangReference = (
  source: MarketSource,
  keyword: string,
  ref: CoupangReference,
) => {
  const key = `${source}::${normalizeProductKey(keyword)}`;
  const cache = readCoupangCache({ includeExpired: true });
  cache[key] = { timestamp: Date.now(), reference: ref };
  writeCoupangCache(cache);
};

const getCoupangRateLimitUntil = (source: MarketSource): number => {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(
      `${MARKET_RATE_LIMIT_KEY_PREFIX}_${source}`,
    );
    const value = Number(raw);
    return Number.isFinite(value) ? value : 0;
  } catch (_) {
    return 0;
  }
};

const setCoupangRateLimitUntil = (source: MarketSource, until: number) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${MARKET_RATE_LIMIT_KEY_PREFIX}_${source}`,
      String(until),
    );
  } catch (_) {}
};

const parseOpenAIMessage = (content: any): string => {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === "string") return part;
        if (typeof part?.text === "string") return part.text;
        return "";
      })
      .join("\n")
      .trim();
  }
  return "";
};

const parseOpenAIResponsesText = (data: any): string => {
  if (typeof data?.output_text === "string" && data.output_text.trim()) {
    return data.output_text.trim();
  }

  const text = (data?.output || [])
    .flatMap((o: any) => o?.content || [])
    .map((c: any) => c?.text || c?.output_text || "")
    .join("\n")
    .trim();

  return text;
};

const isGpt5FamilyModel = (model: string) =>
  /^gpt-5(\.|-|$)/.test(String(model || "").trim());

const getResponsesReasoningEffort = (model: string) => {
  const normalized = String(model || "").trim();
  if (/^gpt-5-pro$/i.test(normalized)) {
    return "high";
  }
  if (/^gpt-5(\.\d+)?-pro$/i.test(normalized)) {
    return "medium";
  }
  if (/^gpt-5-mini(?:-|$)/i.test(normalized)) {
    return "low";
  }
  if (isGpt5FamilyModel(normalized)) {
    return "low";
  }
  return null;
};

const supportsTemperature = (model: string) => !isGpt5FamilyModel(model);
const getOpenAIRequestTimeoutMs = (model: string) => {
  const normalized = String(model || "").trim();
  if (/^gpt-5-pro$/i.test(normalized)) {
    return 45000;
  }
  if (/^gpt-5(\.\d+)?-pro$/i.test(normalized)) {
    return 60000;
  }
  if (/^gpt-5-mini(?:-|$)/i.test(normalized)) {
    return 40000;
  }
  if (isGpt5FamilyModel(normalized)) {
    return 60000;
  }
  return 75000;
};
const OPENAI_CALLABLE_TIMEOUT_BUFFER_MS = 20000;
const OPENAI_CALLABLE_TIMEOUT_CAP_MS = 150000;
const OPENAI_DEFAULT_OUTPUT_TOKENS = 4800;
const OPENAI_MIN_OUTPUT_TOKENS = 3200;
const OPENAI_MAX_OUTPUT_TOKENS = 9000;
const OPENAI_OUTPUT_LIMIT_ERROR_CODE = "openai_max_output_tokens_exceeded";
const GEMINI_RETRYABLE_STATUS_CODES = new Set([429, 500, 503]);
const GEMINI_MAX_RETRIES = 3;
const GEMINI_RETRY_BASE_DELAY_MS = 1500;
const GEMINI_RETRY_MAX_DELAY_MS = 8000;

const getOpenAICallableTimeoutMs = (
  model: string,
  fallbackModel?: string,
) => {
  const models = Array.from(
    new Set(
      [model, fallbackModel]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean),
    ),
  );
  const timeoutBudget = models.reduce(
    (sum, candidate) => sum + getOpenAIRequestTimeoutMs(candidate),
    0,
  );

  return Math.min(
    OPENAI_CALLABLE_TIMEOUT_CAP_MS,
    Math.max(45000, timeoutBudget + OPENAI_CALLABLE_TIMEOUT_BUFFER_MS),
  );
};

const buildOpenAIOutputLimitError = (
  model: string,
  maxOutputTokens?: number,
) => {
  const error = new Error(
    `OpenAI 응답이 출력 토큰 제한으로 중간에 잘렸습니다. (${model}${
      maxOutputTokens ? `, max_output_tokens=${maxOutputTokens}` : ""
    })`,
  ) as Error & { code?: string };
  error.code = OPENAI_OUTPUT_LIMIT_ERROR_CODE;
  return error;
};

const isOpenAIOutputLimitError = (error: any) =>
  String(error?.code || "").trim() === OPENAI_OUTPUT_LIMIT_ERROR_CODE;

const getOpenAIRetryMaxOutputTokens = (value?: number) => {
  const base = Number(value);
  const normalized = Number.isFinite(base)
    ? Math.max(OPENAI_MIN_OUTPUT_TOKENS, Math.round(base))
    : OPENAI_DEFAULT_OUTPUT_TOKENS;
  return Math.min(
    OPENAI_MAX_OUTPUT_TOKENS,
    Math.max(
      OPENAI_MIN_OUTPUT_TOKENS,
      normalized + Math.max(1200, Math.round(normalized * 0.4)),
    ),
  );
};

const getOpenAIMaxOutputTokens = (
  maxItemCount: number,
  stage: "draft" | "revision" | "final" = "draft",
) => {
  const perItemTokens = stage === "final" ? 70 : 90;
  const minTokens = stage === "final" ? 3000 : OPENAI_MIN_OUTPUT_TOKENS;
  const base = Math.min(
    OPENAI_MAX_OUTPUT_TOKENS,
    Math.max(minTokens, 1800 + maxItemCount * perItemTokens),
  );
  if (stage === "final") {
    return Math.min(OPENAI_MAX_OUTPUT_TOKENS, base + 500);
  }
  if (stage === "revision") {
    return Math.min(OPENAI_MAX_OUTPUT_TOKENS, base + 300);
  }
  return base;
};

const getGeminiMaxOutputTokens = (
  maxItemCount: number,
  stage: "draft" | "revision" | "final" = "draft",
) => {
  const perItemTokens = stage === "final" ? 50 : 80;
  const minTokens = stage === "final" ? 2400 : 3600;
  return Math.max(minTokens, 1600 + maxItemCount * perItemTokens);
};

const buildGeminiHttpError = (status: number, message: string) => {
  const error = new Error(
    `Gemini 호출 실패 (${status}): ${message}`,
  ) as Error & { status?: number; retryable?: boolean };
  error.status = status;
  error.retryable = GEMINI_RETRYABLE_STATUS_CODES.has(status);
  return error;
};

const isGeminiRetryableError = (error: any) => Boolean(error?.retryable);
const isGeminiStructuredOutputError = (error: any) =>
  Number(error?.status || 0) === 400;

const getGeminiRetryDelayMs = (attempt: number) =>
  Math.min(
    GEMINI_RETRY_MAX_DELAY_MS,
    GEMINI_RETRY_BASE_DELAY_MS * 2 ** Math.max(0, attempt),
  );

const buildGeminiProposalSchema = (
  targetItemCount: number,
  maxItemCount: number,
) => ({
  type: "object",
  properties: {
    assistant: {
      type: "string",
      enum: ["chatgpt", "gemini"],
    },
    opinion: { type: "string" },
    assumptions: {
      type: "array",
      items: { type: "string" },
      maxItems: 4,
    },
    target_budget: { type: "number" },
    estimated_total_cost: { type: "number" },
    snack_beverage_ratio: { type: "string" },
    items: {
      type: "array",
      minItems: targetItemCount,
      maxItems: maxItemCount,
      items: {
        type: "object",
        properties: {
          product_name: { type: "string" },
          category: {
            type: "string",
            enum: ["snack", "beverage"],
          },
          quantity: { type: "number" },
          unit_price: { type: "number" },
          total_price: { type: "number" },
          reference_source: { type: "string" },
          coupang_reference_price: { type: "number" },
          coupang_search_url: { type: "string" },
          reason: { type: "string" },
        },
        required: [
          "product_name",
          "category",
          "quantity",
          "unit_price",
          "total_price",
          "reference_source",
          "coupang_reference_price",
          "coupang_search_url",
          "reason",
        ],
      },
    },
  },
  required: ["assistant", "opinion", "assumptions", "items"],
});

const buildGeminiFinalSchema = (
  targetItemCount: number,
  maxItemCount: number,
) => ({
  type: "object",
  properties: {
    assistant: {
      type: "string",
      enum: ["chatgpt", "gemini"],
    },
    discussion_summary: { type: "string" },
    opinion: { type: "string" },
    assumptions: {
      type: "array",
      items: { type: "string" },
      maxItems: 3,
    },
    target_budget: { type: "number" },
    items: {
      type: "array",
      minItems: targetItemCount,
      maxItems: maxItemCount,
      items: {
        type: "object",
        properties: {
          product_name: { type: "string" },
          category: {
            type: "string",
            enum: ["snack", "beverage"],
          },
          quantity: { type: "number" },
          unit_price: { type: "number" },
        },
        required: ["product_name", "category", "quantity", "unit_price"],
      },
    },
  },
  required: [
    "assistant",
    "discussion_summary",
    "opinion",
    "assumptions",
    "items",
  ],
});

const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number,
) => {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error: any) {
    if (error?.name === "AbortError") {
      throw new Error(`OpenAI 요청 시간 초과 (${timeoutMs}ms)`);
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timer);
  }
};

const callOpenAI = async ({
  apiKey,
  model,
  fallbackModel,
  system,
  prompt,
  maxOutputTokens,
}: {
  apiKey: string;
  model: string;
  fallbackModel?: string;
  system: string;
  prompt: string;
  maxOutputTokens?: number;
}) => {
  const normalizedFallbackModel = String(fallbackModel || "").trim();

  if (typeof window !== "undefined") {
    try {
      const callableTimeoutMs = getOpenAICallableTimeoutMs(
        model,
        normalizedFallbackModel,
      );
      const callable = httpsCallable<
        {
          model: string;
          fallbackModel?: string;
          system: string;
          prompt: string;
          maxOutputTokens?: number;
        },
        { text?: string; modelUsed?: string }
      >(firebaseFunctions, "openAIChatProxy", {
        timeout: callableTimeoutMs,
      });
      const result = await callable({
        model,
        fallbackModel: normalizedFallbackModel || undefined,
        system,
        prompt,
        maxOutputTokens,
      });
      const text = String(result.data?.text || "").trim();
      if (!text) {
        throw new Error("OpenAI 프록시 함수 응답에 text가 없습니다.");
      }
      return text;
    } catch (error: any) {
      const message = String(error?.message || error || "").trim();
      throw new Error(
        message ||
          "OpenAI 프록시 함수 호출에 실패했습니다. 함수 배포 및 OPENAI_API_KEY_SECRET 시크릿 설정을 확인하세요.",
      );
    }
  }

  const runOnce = async (targetModel: string, outputTokenBudget?: number) => {
    const useResponsesOnly = isGpt5FamilyModel(targetModel);
    const reasoningEffort = getResponsesReasoningEffort(targetModel);
    const includeTemperature = supportsTemperature(targetModel);
    const requestTimeoutMs = getOpenAIRequestTimeoutMs(targetModel);
    let chatErrorMessage = "";

    let chatStatus = 0;
    if (!useResponsesOnly) {
      const chatResponse = await fetchWithTimeout(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model: targetModel,
            ...(includeTemperature ? { temperature: 0.3 } : {}),
            ...(outputTokenBudget ? { max_tokens: outputTokenBudget } : {}),
            response_format: {
              type: "json_object",
            },
            messages: [
              { role: "system", content: system },
              { role: "user", content: prompt },
            ],
          }),
        },
        requestTimeoutMs,
      );

      chatStatus = chatResponse.status;
      if (chatResponse.ok) {
        const data = await chatResponse.json();
        const finishReason = String(data?.choices?.[0]?.finish_reason || "").trim();
        if (finishReason === "length") {
          throw buildOpenAIOutputLimitError(targetModel, outputTokenBudget);
        }
        const text = parseOpenAIMessage(data?.choices?.[0]?.message?.content);
        if (text) return text;
      } else {
        chatErrorMessage = await chatResponse.text();
      }
    }

    const responsesApiResponse = await fetchWithTimeout(
      "https://api.openai.com/v1/responses",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: targetModel,
          ...(includeTemperature ? { temperature: 0.3 } : {}),
          text: {
            format: {
              type: "json_object",
            },
            verbosity: "low",
          },
          ...(useResponsesOnly
            ? {
                reasoning: {
                  effort: reasoningEffort,
                },
                ...(outputTokenBudget
                  ? { max_output_tokens: outputTokenBudget }
                  : {}),
              }
            : {}),
          input: [
            { role: "system", content: system },
            { role: "user", content: prompt },
          ],
        }),
      },
      requestTimeoutMs,
    );

    if (!responsesApiResponse.ok) {
      const responsesError = await responsesApiResponse.text();
      throw new Error(
        `OpenAI 호출 실패(${targetModel}; chat=${chatStatus}, responses=${responsesApiResponse.status}): ${
          responsesError || chatErrorMessage
        }`,
      );
    }

    const responsesData = await responsesApiResponse.json();
    if (
      responsesData?.status === "incomplete" &&
      responsesData?.incomplete_details?.reason === "max_output_tokens"
    ) {
      throw buildOpenAIOutputLimitError(targetModel, outputTokenBudget);
    }
    const responsesText = parseOpenAIResponsesText(responsesData);
    if (!responsesText) {
      throw new Error(`OpenAI 응답에서 텍스트를 찾지 못했습니다. (${targetModel})`);
    }
    return responsesText;
  };

  const runWithRetry = async (targetModel: string) => {
    try {
      return await runOnce(targetModel, maxOutputTokens);
    } catch (error) {
      if (!isOpenAIOutputLimitError(error)) {
        throw error;
      }
      const retryMaxOutputTokens = getOpenAIRetryMaxOutputTokens(
        maxOutputTokens,
      );
      if (
        Number.isFinite(Number(maxOutputTokens)) &&
        retryMaxOutputTokens <= Number(maxOutputTokens)
      ) {
        throw error;
      }
      return runOnce(targetModel, retryMaxOutputTokens);
    }
  };

  try {
    return await runWithRetry(model);
  } catch (primaryError) {
    if (!normalizedFallbackModel || normalizedFallbackModel === model) {
      throw primaryError;
    }
    return await runWithRetry(normalizedFallbackModel);
  }
};

const callGemini = async ({
  apiKey,
  model,
  fallbackModel,
  prompt,
  maxOutputTokens,
  responseJsonSchema,
}: {
  apiKey: string;
  model: string;
  fallbackModel?: string;
  prompt: string;
  maxOutputTokens?: number;
  responseJsonSchema?: Record<string, any>;
}) => {
  const runRequest = async (
    targetModel: string,
    useStructuredOutput: boolean,
  ) => {
    const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
      targetModel,
    )}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [
          {
            role: "user",
            parts: [{ text: prompt }],
          },
        ],
        generationConfig: {
          temperature: 0.3,
          ...(maxOutputTokens ? { maxOutputTokens } : {}),
          ...(useStructuredOutput && responseJsonSchema
            ? {
                responseMimeType: "application/json",
                responseJsonSchema,
              }
            : {}),
        },
      }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw buildGeminiHttpError(response.status, message);
    }

    const data = await response.json();
    const candidate = data?.candidates?.[0];
    const finishReason = String(candidate?.finishReason || "").trim();
    const finishMessage = String(candidate?.finishMessage || "").trim();
    const text: string = (data?.candidates || [])
      .flatMap((c: any) => c?.content?.parts || [])
      .map((p: any) => p?.text || "")
      .join("\n")
      .trim();

    if (
      finishReason &&
      finishReason !== "STOP" &&
      finishReason !== "FINISH_REASON_UNSPECIFIED"
    ) {
      throw new Error(
        `Gemini 응답 중단 (${finishReason})${
          finishMessage ? `: ${finishMessage}` : ""
        }`,
      );
    }

    if (!text) {
      throw new Error("Gemini 응답에서 텍스트를 찾지 못했습니다.");
    }
    return text;
  };

  const runModel = async (targetModel: string) => {
    let useStructuredOutput = Boolean(responseJsonSchema);

    for (let attempt = 0; attempt <= GEMINI_MAX_RETRIES; attempt += 1) {
      try {
        return await runRequest(targetModel, useStructuredOutput);
      } catch (error: any) {
        if (useStructuredOutput && isGeminiStructuredOutputError(error)) {
          useStructuredOutput = false;
          continue;
        }
        if (
          isGeminiRetryableError(error) &&
          attempt < GEMINI_MAX_RETRIES
        ) {
          await sleep(getGeminiRetryDelayMs(attempt));
          continue;
        }
        throw error;
      }
    }
    throw new Error(`Gemini 호출 재시도에 실패했습니다. (${targetModel})`);
  };

  try {
    return await runModel(model);
  } catch (primaryError) {
    const normalizedFallbackModel = String(fallbackModel || "").trim();
    if (!normalizedFallbackModel || normalizedFallbackModel === model) {
      throw primaryError;
    }
    return runModel(normalizedFallbackModel);
  }
};

const fetchMarketReference = async (
  source: MarketSource,
  productName: string,
): Promise<CoupangFetchResult> => {
  const keyword = String(productName || "").trim();
  if (!keyword) {
    return { ref: null, rateLimited: false, fromCache: false };
  }

  const cached = getCachedCoupangReference(source, keyword, {
    allowStale: false,
  });
  if (cached.reference) {
    return { ref: cached.reference, rateLimited: false, fromCache: true };
  }

  if (Date.now() < getCoupangRateLimitUntil(source)) {
    return { ref: null, rateLimited: true, fromCache: false };
  }

  const proxyUrl = buildMarketProxyUrl(source, keyword);
  const fetchViaFunction = async () => {
    const callable = httpsCallable<
      { mode: string; source: MarketSource; keyword: string },
      { ok?: boolean; status?: number; text?: string; errorText?: string }
    >(firebaseFunctions, "openAIChatProxy");
    const result = await callable({
      mode: "market_reference",
      source,
      keyword,
    });
    return {
      ok: Boolean(result.data?.ok),
      status: Number(result.data?.status || 0),
      text: String(result.data?.text || ""),
      errorText: String(result.data?.errorText || ""),
    };
  };

  for (let attempt = 0; attempt <= MARKET_RETRY_COUNT; attempt += 1) {
    try {
      const response =
        typeof window !== "undefined"
          ? await fetchViaFunction()
          : await fetch(proxyUrl, {
              method: "GET",
              headers: {
                Accept: "text/plain",
              },
            }).then(async (res) => ({
              ok: res.ok,
              status: res.status,
              text: res.ok ? await res.text() : "",
              errorText: res.ok ? "" : await res.text(),
            }));

      if (response.status === 429) {
        if (attempt < MARKET_RETRY_COUNT) {
          await sleep(MARKET_RETRY_BASE_DELAY_MS * (attempt + 1));
          continue;
        }
        setCoupangRateLimitUntil(
          source,
          Date.now() + MARKET_RATE_LIMIT_COOLDOWN_MS,
        );
        return { ref: null, rateLimited: true, fromCache: false };
      }

      if (!response.ok) {
        return { ref: null, rateLimited: false, fromCache: false };
      }

      const text = response.text;
      const priceSamples = extractCoupangPrices(text);
      const medianPrice = median(priceSamples);

      if (!medianPrice) {
        return { ref: null, rateLimited: false, fromCache: false };
      }

      const reference: CoupangReference = {
        source,
        source_label: MARKET_SOURCE_CONFIG[source].label,
        product_name: keyword,
        search_keyword: keyword,
        search_url: buildMarketSearchUrl(source, keyword),
        price_samples: priceSamples,
        median_price: medianPrice,
      };
      setCachedCoupangReference(source, keyword, reference);
      return { ref: reference, rateLimited: false, fromCache: false };
    } catch (_) {
      if (attempt < MARKET_RETRY_COUNT) {
        await sleep(MARKET_RETRY_BASE_DELAY_MS * (attempt + 1));
        continue;
      }
      return { ref: null, rateLimited: false, fromCache: false };
    }
  }

  return { ref: null, rateLimited: false, fromCache: false };
};

export const loadCoupangReferences = async (
  products: ProductSummary[],
): Promise<CoupangLoadResult> => {
  const uniqueMap = new Map<string, ProductSummary>();
  products.forEach((product) => {
    const key = normalizeProductKey(product.product_name);
    if (!key || uniqueMap.has(key)) return;
    uniqueMap.set(key, product);
  });

  const candidates = Array.from(uniqueMap.values()).slice(
    0,
    MARKET_MAX_REFERENCE_PRODUCTS,
  );

  const references: CoupangReference[] = [];
  const seen = new Set<string>();
  let rateLimited = false;
  let fromCacheCount = 0;
  let staleCacheCount = 0;
  let networkCallCount = 0;
  const missingPairs: Array<{ source: MarketSource; productName: string }> = [];
  const pairKey = (source: MarketSource, productName: string) =>
    `${normalizeProductKey(productName)}::${source}`;

  for (const product of candidates) {
    for (const source of MARKET_SOURCE_ORDER) {
      const key = pairKey(source, product.product_name);
      const cachedResult = getCachedCoupangReference(source, product.product_name, {
        allowStale: false,
      });
      if (!cachedResult.reference) {
        missingPairs.push({ source, productName: product.product_name });
        continue;
      }
      if (!seen.has(key)) {
        references.push(cachedResult.reference);
        seen.add(key);
        fromCacheCount += 1;
      }
    }
  }

  const attempted = candidates.length * MARKET_SOURCE_ORDER.length;
  const networkTasks = missingPairs.slice(0, MARKET_MAX_NETWORK_CALLS);

  for (let i = 0; i < networkTasks.length; i += 1) {
    const task = networkTasks[i];
    networkCallCount += 1;
    const result = await fetchMarketReference(task.source, task.productName);
    const key = pairKey(task.source, task.productName);
    if (result.ref) {
      if (!seen.has(key)) {
        references.push(result.ref);
        seen.add(key);
      }
      if (result.fromCache) {
        fromCacheCount += 1;
      }
    }

    if (result.rateLimited) {
      rateLimited = true;
    }

    if (i < networkTasks.length - 1) {
      await sleep(MARKET_FETCH_DELAY_MS);
    }
  }

  for (const task of missingPairs) {
    const key = pairKey(task.source, task.productName);
    if (seen.has(key)) continue;
    const staleResult = getCachedCoupangReference(task.source, task.productName, {
      allowStale: true,
    });
    if (!staleResult.reference || !staleResult.stale) continue;
    references.push(staleResult.reference);
    seen.add(key);
    staleCacheCount += 1;
  }

  return {
    references,
    rateLimited,
    attempted,
    fromCacheCount,
    staleCacheCount,
    networkCallCount,
  };
};

const monthsBetween = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
  const list: string[] = [];
  let cursor = start.startOf("month");
  const stop = end.startOf("month");
  while (cursor.isSameOrBefore(stop, "month")) {
    list.push(cursor.format("YYYY-MM"));
    cursor = cursor.add(1, "month");
  }
  return list;
};

export const loadSalesRows = async (
  targetAffiliations: string[],
  range: SalesRange,
) => {
  if (targetAffiliations.length === 0) return [];
  const start = range?.[0] || null;
  const end = range?.[1] || null;

  const snapshots = range
    ? await Promise.all(
        targetAffiliations.flatMap((affiliation) =>
          monthsBetween(range[0], range[1]).map((monthKey) =>
            getDoc(
              doc(db, `affiliations/${affiliation}/salesUploads/${monthKey}`),
            ).then((snap) => ({ snap, affiliation })),
          ),
        ),
      )
    : (
        await Promise.all(
          targetAffiliations.map(async (affiliation) => {
            const snap = await getDocs(
              collection(db, `affiliations/${affiliation}/salesUploads`),
            );
            return snap.docs.map((docSnap) => ({ snap: docSnap, affiliation }));
          }),
        )
      ).flat();

  const rows: SalesRow[] = [];

  snapshots.forEach(({ snap, affiliation }) => {
    if (!snap.exists()) return;
    const data = snap.data() as any;
    const rawRows: any[] = Array.isArray(data?.rows) ? data.rows : [];

    rawRows.forEach((row: any) => {
      const saleDate = String(row?.saleDate ?? "");
      if (!saleDate) return;

      const dt = dayjs(saleDate);
      if (!dt.isValid()) return;
      if (
        start &&
        end &&
        (!dt.isSameOrAfter(start, "day") || !dt.isSameOrBefore(end, "day"))
      ) {
        return;
      }

      const quantity = Math.max(0, toNum(row?.quantity, 0));
      const unitPrice = Math.max(0, toNum(row?.unitPrice, 0));
      const totalPrice = Math.max(
        0,
        toNum(row?.totalPrice, unitPrice * quantity),
      );

      if (!row?.productName || quantity <= 0) return;

      rows.push({
        saleDate,
        productName: String(row.productName),
        unitPrice,
        quantity,
        totalPrice,
        receiptNumber: String(row?.receiptNumber ?? ""),
        affiliation,
      });
    });
  });

  return rows;
};

export const loadWarehouseInventorySummary = async (
): Promise<WarehouseInventorySummaryInput> => {
  const aggregate = new Map<string, WarehouseStockSummary>();
  const appendRows = (rows: any[]) => {
    rows.forEach((row: any) => {
      const productName = String(
        row?.itemName ?? row?.product_name ?? "",
      ).trim();
      if (!productName) return;

      const rawStock =
        row?.stockCount ?? row?.stock_count ?? row?.stockText ?? row?.stock_text;
      const parsedStock = Number(String(rawStock ?? "").replace(/,/g, "").trim());
      const stockCount = Math.max(
        0,
        Math.round(Number.isFinite(parsedStock) ? parsedStock : 0),
      );
      if (stockCount <= 0) return;

      const key = normalizeProductKey(productName);
      if (!key) return;

      const itemCode = String(row?.itemCode ?? row?.item_code ?? "").trim();
      const spec = String(row?.spec ?? "").trim();
      const prev = aggregate.get(key);

      if (!prev) {
        aggregate.set(key, {
          product_name: productName,
          item_code: itemCode,
          spec,
          stock_count: stockCount,
        });
        return;
      }

      prev.stock_count += stockCount;
      if (!prev.item_code && itemCode) prev.item_code = itemCode;
      if (!prev.spec && spec) prev.spec = spec;
      aggregate.set(key, prev);
    });
  };

  const snapshot = await getDoc(doc(db, "warehouseInventory/current"));
  if (snapshot.exists()) {
    const data = snapshot.data() as any;
    const rows: any[] = Array.isArray(data?.items) ? data.items : [];
    appendRows(rows);
  }

  if (aggregate.size === 0) {
    const fallbackSnap = await getDoc(
      doc(db, "affiliations/총무노트/warehouseInventory/current"),
    );
    if (fallbackSnap.exists()) {
      const fallbackData = fallbackSnap.data() as any;
      const fallbackRows: any[] = Array.isArray(fallbackData?.items)
        ? fallbackData.items
        : [];
      appendRows(fallbackRows);
    }
  }

  const allProducts = Array.from(aggregate.values()).sort((a, b) => {
    if (a.stock_count !== b.stock_count) {
      return b.stock_count - a.stock_count;
    }
    return a.product_name.localeCompare(b.product_name, "ko");
  });
  const totalStock = allProducts.reduce((acc, item) => acc + item.stock_count, 0);

  return {
    products: allProducts.slice(0, 80),
    itemCount: allProducts.length,
    totalStock,
  };
};

export const loadSupplementalCurationData = async (
  seedProductNames: string[] = [],
): Promise<SupplementalCurationData> => {
  const loadedSheets = await Promise.all(
    CURATION_SHEET_SPECS.map(async (spec): Promise<SheetLoadResult> => {
      try {
        const rows = await fetchSheetRows(spec);
        return { spec, rows, error: null };
      } catch (error: any) {
        return {
          spec,
          rows: [],
          error:
            error instanceof Error
              ? error
              : new Error(`${spec.label} 시트 로딩 실패`),
        };
      }
    }),
  );

  const failed_sources = loadedSheets
    .filter((sheet) => sheet.error)
    .map((sheet) => sheet.spec.label);
  const inboundRows =
    loadedSheets.find((sheet) => sheet.spec.key === "inbound")?.rows || [];
  const outboundRows =
    loadedSheets.find((sheet) => sheet.spec.key === "outbound")?.rows || [];
  const requestRows =
    loadedSheets.find((sheet) => sheet.spec.key === "request")?.rows || [];
  const markupRows =
    loadedSheets.find((sheet) => sheet.spec.key === "markup")?.rows || [];

  const seedNames = [
    ...seedProductNames,
    ...inboundRows.map((row) => String(row["품명"] || "").trim()),
    ...outboundRows.map((row) => String(row["상품명"] || "").trim()),
    ...markupRows.map((row) => String(row["품명"] || "").trim()),
  ].filter(Boolean);

  const exactMap = new Map<string, string>();
  const catalog: Array<{ key: string; name: string }> = [];
  seedNames.forEach((name) => {
    const trimmed = String(name || "").trim();
    const key = normalizeLooseProductKey(trimmed);
    if (!key || exactMap.has(key)) return;
    exactMap.set(key, trimmed);
    catalog.push({ key, name: trimmed });
  });

  const aggregate = new Map<string, ProductSignalAccumulator>();
  const ensureSignal = (productName: string) => {
    const canonicalName = resolveCanonicalProductName(productName, catalog, exactMap);
    const key = normalizeLooseProductKey(canonicalName);
    if (!key) return null;

    const existing = aggregate.get(key);
    if (existing) return existing;

    const next: ProductSignalAccumulator = {
      product_name: canonicalName,
      inbound_quantity: 0,
      outbound_quantity: 0,
      outbound_customers: new Set<string>(),
      request_count: 0,
      request_companies: new Set<string>(),
      markup_percent: null,
      cost_price: null,
      sale_price: null,
      coupang_lowest_price: null,
    };
    aggregate.set(key, next);
    return next;
  };

  inboundRows.forEach((row) => {
    const signal = ensureSignal(String(row["품명"] || ""));
    if (!signal) return;

    signal.inbound_quantity += Math.max(0, Math.round(parseLooseNumber(row["입고수량"]) || 0));
  });

  outboundRows.forEach((row) => {
    const signal = ensureSignal(String(row["상품명"] || ""));
    if (!signal) return;

    signal.outbound_quantity += Math.max(0, Math.round(parseLooseNumber(row["출고수량"]) || 0));
    const customerName = normalizeCompanyName(row["고객사명"]);
    if (customerName) {
      signal.outbound_customers.add(customerName);
    }
  });

  markupRows.forEach((row) => {
    const signal = ensureSignal(String(row["품명"] || ""));
    if (!signal) return;

    const markupPercent = parseLooseNumber(row["MARKUP"]);
    const costPrice = parseLooseNumber(row["단가"]);
    const salePrice = parseLooseNumber(row["판매가"]);
    const coupangLowestPrice = parseLooseNumber(row["쿠팡최저가"]);

    signal.markup_percent = markupPercent ?? signal.markup_percent;
    signal.cost_price = costPrice ?? signal.cost_price;
    signal.sale_price = salePrice ?? signal.sale_price;
    signal.coupang_lowest_price = coupangLowestPrice ?? signal.coupang_lowest_price;
  });

  const requestKeywordMap = new Map<
    string,
    { keyword: string; count: number; companies: Set<string> }
  >();
  let requestMatchedItems = 0;

  requestRows.forEach((row) => {
    const company = normalizeCompanyName(row["회사명"]);
    splitRequestedItems(row["희망하는 간식"]).forEach((keyword) => {
      const canonicalName = resolveCanonicalProductName(keyword, catalog, exactMap);
      const signal = ensureSignal(canonicalName);
      const resolvedKeyword = canonicalName || keyword;
      const requestKey = normalizeLooseProductKey(resolvedKeyword) || resolvedKeyword;

      if (signal) {
        signal.request_count += 1;
        if (company) signal.request_companies.add(company);
        requestMatchedItems += 1;
      }

      const existing = requestKeywordMap.get(requestKey) || {
        keyword: resolvedKeyword,
        count: 0,
        companies: new Set<string>(),
      };
      existing.count += 1;
      if (company) existing.companies.add(company);
      requestKeywordMap.set(requestKey, existing);
    });
  });

  const products: SupplementalProductSignal[] = Array.from(aggregate.values())
    .map((signal) => {
      const remainingStockQuantity = Math.max(
        0,
        signal.inbound_quantity - signal.outbound_quantity,
      );
      return {
        product_name: signal.product_name,
        inbound_quantity: signal.inbound_quantity,
        outbound_quantity: signal.outbound_quantity,
        remaining_stock_quantity: remainingStockQuantity,
        outbound_customer_count: signal.outbound_customers.size,
        request_count: signal.request_count,
        request_company_count: signal.request_companies.size,
        markup_percent: signal.markup_percent,
        cost_price: signal.cost_price,
        sale_price: signal.sale_price,
        coupang_lowest_price: signal.coupang_lowest_price,
      };
    })
    .sort((a, b) => {
      const aScore = buildProductPriorityScore({
        totalQuantity: 0,
        totalSales: 0,
        inboundQuantity: a.inbound_quantity,
        outboundQuantity: a.outbound_quantity,
        remainingStockQuantity: a.remaining_stock_quantity,
        outboundCustomerCount: a.outbound_customer_count,
        requestCount: a.request_count,
        requestCompanyCount: a.request_company_count,
        markupPercent: a.markup_percent,
      });
      const bScore = buildProductPriorityScore({
        totalQuantity: 0,
        totalSales: 0,
        inboundQuantity: b.inbound_quantity,
        outboundQuantity: b.outbound_quantity,
        remainingStockQuantity: b.remaining_stock_quantity,
        outboundCustomerCount: b.outbound_customer_count,
        requestCount: b.request_count,
        requestCompanyCount: b.request_company_count,
        markupPercent: b.markup_percent,
      });
      if (aScore !== bScore) return bScore - aScore;
      return a.product_name.localeCompare(b.product_name, "ko");
    });

  const requested_keywords = Array.from(requestKeywordMap.values())
    .map((item) => ({
      keyword: item.keyword,
      count: item.count,
      companies: Array.from(item.companies).sort((a, b) =>
        a.localeCompare(b, "ko"),
      ),
    }))
    .sort((a, b) => {
      if (a.count !== b.count) return b.count - a.count;
      return a.keyword.localeCompare(b.keyword, "ko");
    })
    .slice(0, 20);

  const high_margin_products: HighMarginProductSummary[] = markupRows
    .map((row) => {
      const canonicalName = resolveCanonicalProductName(String(row["품명"] || ""), catalog, exactMap);
      const markupPercent = parseLooseNumber(row["MARKUP"]);
      return {
        product_name: canonicalName,
        markup_percent: Math.max(0, Math.round(markupPercent || 0)),
        cost_price: parseLooseNumber(row["단가"]),
        sale_price: parseLooseNumber(row["판매가"]),
        coupang_lowest_price: parseLooseNumber(row["쿠팡최저가"]),
      };
    })
    .filter((item) => item.product_name && item.markup_percent > 0)
    .sort((a, b) => {
      if (a.markup_percent !== b.markup_percent) {
        return b.markup_percent - a.markup_percent;
      }
      return a.product_name.localeCompare(b.product_name, "ko");
    })
    .slice(0, 20);

  return {
    inbound_rows: inboundRows.length,
    outbound_rows: outboundRows.length,
    request_rows: requestRows.length,
    request_matched_items: requestMatchedItems,
    markup_rows: markupRows.length,
    remaining_stock_items: products.filter(
      (product) => product.remaining_stock_quantity > 0,
    ).length,
    remaining_stock_total_qty: products.reduce(
      (sum, product) => sum + product.remaining_stock_quantity,
      0,
    ),
    requested_keywords,
    high_margin_products,
    products,
    failed_sources,
  };
};

export const buildDatasetSummary = (
  affiliation: string,
  range: SalesRange,
  rows: SalesRow[],
  coupangReferences: CoupangReference[],
  supplementalData: SupplementalCurationData = EMPTY_SUPPLEMENTAL_DATA,
): DatasetSummary => {
  const productMap = new Map<
    string,
    {
      productName: string;
      qty: number;
      sales: number;
      unitPriceSum: number;
      count: number;
      supplemental: SupplementalProductSignal | null;
    }
  >();
  let totalQty = 0;
  let totalSales = 0;
  let minSaleDate: Dayjs | null = null;
  let maxSaleDate: Dayjs | null = null;

  const ensureProductStats = (productName: string) => {
    const key = normalizeLooseProductKey(productName);
    if (!key) return null;

    const existing = productMap.get(key);
    if (existing) return { key, stats: existing };

    const next = {
      productName,
      qty: 0,
      sales: 0,
      unitPriceSum: 0,
      count: 0,
      supplemental: null as SupplementalProductSignal | null,
    };
    productMap.set(key, next);
    return { key, stats: next };
  };

  rows.forEach((row) => {
    const normalized = ensureProductStats(row.productName);
    if (!normalized) return;

    const { stats } = normalized;
    stats.qty += row.quantity;
    stats.sales += row.totalPrice;
    stats.unitPriceSum += row.unitPrice;
    stats.count += 1;

    totalQty += row.quantity;
    totalSales += row.totalPrice;

    const saleDate = dayjs(row.saleDate);
    if (saleDate.isValid()) {
      if (!minSaleDate || saleDate.isBefore(minSaleDate, "day")) {
        minSaleDate = saleDate;
      }
      if (!maxSaleDate || saleDate.isAfter(maxSaleDate, "day")) {
        maxSaleDate = saleDate;
      }
    }
  });

  supplementalData.products.forEach((signal) => {
    const normalized = ensureProductStats(signal.product_name);
    if (!normalized) return;
    const { stats } = normalized;
    if (!stats.productName && signal.product_name) {
      stats.productName = signal.product_name;
    }
    stats.supplemental = signal;
  });

  const topProducts: ProductSummary[] = Array.from(productMap.entries())
    .map(([, stats]) => {
      const signal = stats.supplemental;
      const avgUnitPrice = Math.round(
        stats.qty > 0
          ? stats.sales / stats.qty
          : signal?.sale_price || signal?.coupang_lowest_price || signal?.cost_price || 0,
      );
      const remainingStockQuantity = signal?.remaining_stock_quantity || 0;

      return {
        product_name: stats.productName,
        category_hint: inferCategory(stats.productName),
        total_quantity: Math.round(stats.qty),
        total_sales: Math.round(stats.sales),
        avg_unit_price: Math.max(0, avgUnitPrice),
        order_count: stats.count,
        warehouse_stock_count: 0,
        inbound_quantity: signal?.inbound_quantity || 0,
        outbound_quantity: signal?.outbound_quantity || 0,
        remaining_stock_quantity: remainingStockQuantity,
        outbound_customer_count: signal?.outbound_customer_count || 0,
        request_count: signal?.request_count || 0,
        request_company_count: signal?.request_company_count || 0,
        markup_percent: signal?.markup_percent ?? null,
        curation_priority_score: buildProductPriorityScore({
          totalQuantity: Math.round(stats.qty),
          totalSales: Math.round(stats.sales),
          inboundQuantity: signal?.inbound_quantity || 0,
          outboundQuantity: signal?.outbound_quantity || 0,
          remainingStockQuantity,
          outboundCustomerCount: signal?.outbound_customer_count || 0,
          requestCount: signal?.request_count || 0,
          requestCompanyCount: signal?.request_company_count || 0,
          markupPercent: signal?.markup_percent ?? null,
        }),
      };
    })
    .filter((product) => {
      if (product.total_quantity > 0) return true;
      if (product.remaining_stock_quantity > 0) return true;
      if (product.inbound_quantity > 0) return true;
      if (product.outbound_quantity > 0) return true;
      if (product.request_count > 0) return true;
      return (product.markup_percent ?? 0) > 0;
    })
    .sort((a, b) => {
      if (a.curation_priority_score !== b.curation_priority_score) {
        return b.curation_priority_score - a.curation_priority_score;
      }
      if (a.total_quantity !== b.total_quantity) {
        return b.total_quantity - a.total_quantity;
      }
      if (a.outbound_quantity !== b.outbound_quantity) {
        return b.outbound_quantity - a.outbound_quantity;
      }
      if (a.request_count !== b.request_count) {
        return b.request_count - a.request_count;
      }
      return a.product_name.localeCompare(b.product_name, "ko");
    })
    .slice(0, 80);

  const receiptSet = new Set(
    rows
      .map((row) =>
        row.receiptNumber ? `${row.affiliation}::${row.receiptNumber}` : "",
      )
      .filter((x) => x.length > 0),
  );
  const periodStart = range
    ? range[0].format("YYYY-MM-DD")
    : minSaleDate
      ? (minSaleDate as Dayjs).format("YYYY-MM-DD")
      : "";
  const periodEnd = range
    ? range[1].format("YYYY-MM-DD")
    : maxSaleDate
      ? (maxSaleDate as Dayjs).format("YYYY-MM-DD")
      : "";

  return {
    affiliation,
    period_mode: range ? "custom" : "all",
    period_start: periodStart,
    period_end: periodEnd,
    source_rows: rows.length,
    source_receipts: receiptSet.size,
    source_total_qty: Math.round(totalQty),
    source_total_sales: Math.round(totalSales),
    inbound_rows: supplementalData.inbound_rows,
    outbound_rows: supplementalData.outbound_rows,
    request_rows: supplementalData.request_rows,
    request_matched_items: supplementalData.request_matched_items,
    markup_rows: supplementalData.markup_rows,
    remaining_stock_items: supplementalData.remaining_stock_items,
    remaining_stock_total_qty: supplementalData.remaining_stock_total_qty,
    warehouse_inventory_items: 0,
    warehouse_inventory_total_stock: 0,
    warehouse_inventory_matched_products: 0,
    warehouse_top_products: [],
    top_products: topProducts,
    requested_keywords: supplementalData.requested_keywords,
    high_margin_products: supplementalData.high_margin_products,
    coupang_references: coupangReferences,
  };
};

export const runDebate = async ({
  requirement,
  datasetSummary,
  openAIEnabled = false,
  openAIApiKey,
  openAIModel,
  openAIFallbackModel,
  geminiApiKey,
  geminiModel,
  geminiFallbackModel = "",
  customerFacing = false,
  onLog,
  onProgress,
}: {
  requirement: string;
  datasetSummary: DatasetSummary;
  openAIEnabled?: boolean;
  openAIApiKey: string;
  openAIModel: string;
  openAIFallbackModel: string;
  geminiApiKey: string;
  geminiModel: string;
  geminiFallbackModel?: string;
  customerFacing?: boolean;
  onLog?: (level: LogLevel, message: string) => void;
  onProgress?: (message: string | null) => void;
}): Promise<DebateResult> => {
  const system =
    "너는 판매, 재고, 입출고, 고객 요청, 마진 데이터를 종합해서 추천하는 상품 큐레이션 전문가다.";
  const hasOpenAI = Boolean(openAIEnabled || openAIApiKey.trim());
  const hasGemini = Boolean(geminiApiKey.trim());
  if (!hasOpenAI && !hasGemini) {
    throw new Error("Gemini 또는 OpenAI API 키가 필요합니다.");
  }

  const describeProviderError = (error: any) =>
    String(error?.message || error || "알 수 없는 오류")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 320);
  const summarizeRawModelOutput = (raw: string) =>
    String(raw || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 280);
  const parseModelJson = (
    raw: string,
    provider: "openai" | "gemini",
    stageLabel: string,
  ) => {
    try {
      return extractJson(raw);
    } catch (error) {
      const preview = summarizeRawModelOutput(raw);
      throw new Error(
        `${provider === "openai" ? "OpenAI" : "Gemini"} ${stageLabel} 응답 JSON 파싱 실패${
          preview ? `: ${preview}` : ""
        }`,
      );
    }
  };
  const explicitItemCount = parseTargetItemCountFromRequirement(requirement);
  const targetItemCount = computeTargetItemCount(requirement, datasetSummary);
  const maxItemCount = Math.max(
    Math.min(CURATION_MAX_ITEMS, targetItemCount + 12),
    24,
  );
  const openAIDraftMaxOutputTokens = getOpenAIMaxOutputTokens(
    maxItemCount,
    "draft",
  );
  const openAIRevisionMaxOutputTokens = getOpenAIMaxOutputTokens(
    maxItemCount,
    "revision",
  );
  const openAIFinalMaxOutputTokens = getOpenAIMaxOutputTokens(
    maxItemCount,
    "final",
  );
  const geminiDraftMaxOutputTokens = getGeminiMaxOutputTokens(
    maxItemCount,
    "draft",
  );
  const geminiRevisionMaxOutputTokens = getGeminiMaxOutputTokens(
    maxItemCount,
    "revision",
  );
  const geminiFinalMaxOutputTokens = getGeminiMaxOutputTokens(
    maxItemCount,
    "final",
  );
  onLog?.(
    "info",
    explicitItemCount != null
      ? `품목 수 고정 요청 감지 (${targetItemCount}개 정확히 구성)`
      : `품목 다양성 목표 설정 (최소 ${targetItemCount}개, 최대 ${maxItemCount}개)`,
  );
  const draftPrompt = buildOpenAIDraftPrompt(
    requirement,
    datasetSummary,
    targetItemCount,
    maxItemCount,
    customerFacing,
  );

  const buildDraft = async (): Promise<{
    parsed: any;
    provider: "openai" | "gemini";
  }> => {
    if (hasOpenAI) {
      try {
        const raw = await callOpenAI({
          apiKey: openAIApiKey,
          model: openAIModel,
          fallbackModel: openAIFallbackModel,
          system,
          prompt: draftPrompt,
          maxOutputTokens: openAIDraftMaxOutputTokens,
        });
        return {
          parsed: parseModelJson(raw, "openai", "초안"),
          provider: "openai",
        };
      } catch (error) {
        const reason = describeProviderError(error);
        onLog?.(
          "warn",
          hasGemini
            ? `OpenAI 초안 생성 실패 (${reason}). Gemini로 대체합니다.`
            : `OpenAI 초안 생성 실패 (${reason})`,
        );
        if (!hasGemini) throw error;
      }
    }

    const raw = await callGemini({
      apiKey: geminiApiKey,
      model: geminiModel,
      fallbackModel: geminiFallbackModel,
      prompt: draftPrompt,
      maxOutputTokens: geminiDraftMaxOutputTokens,
      responseJsonSchema: buildGeminiProposalSchema(
        targetItemCount,
        maxItemCount,
      ),
    });
    return {
      parsed: parseModelJson(raw, "gemini", "초안"),
      provider: "gemini",
    };
  };

  onProgress?.("1/3 ChatGPT 초안 생성");
  onLog?.("info", "1/3 ChatGPT 초안 생성 요청");
  const draftResult = await buildDraft();
  const chatgptDraft = normalizeProposal(
    draftResult.parsed,
    "chatgpt",
    datasetSummary.coupang_references,
    {
      datasetSummary,
      targetItemCount,
      maxItemCount,
      requirement,
      customerFacing,
    },
  );
  onLog?.(
    "success",
    `1/3 초안 완료 (${chatgptDraft.items.length}개 품목, ${
      draftResult.provider === "openai" ? "OpenAI" : "Gemini 대체"
    })`,
  );

  const revisionPrompt = buildGeminiRevisionPrompt(
    requirement,
    datasetSummary,
    chatgptDraft,
    targetItemCount,
    maxItemCount,
    customerFacing,
  );
  const buildRevision = async (): Promise<{
    parsed: any;
    provider: "openai" | "gemini";
  }> => {
    if (hasGemini) {
      try {
        const raw = await callGemini({
          apiKey: geminiApiKey,
          model: geminiModel,
          fallbackModel: geminiFallbackModel,
          prompt: revisionPrompt,
          maxOutputTokens: geminiRevisionMaxOutputTokens,
          responseJsonSchema: buildGeminiProposalSchema(
            targetItemCount,
            maxItemCount,
          ),
        });
        return {
          parsed: parseModelJson(raw, "gemini", "수정안"),
          provider: "gemini",
        };
      } catch (error) {
        onLog?.(
          "warn",
          `Gemini 수정안 생성 실패. ${
            hasOpenAI ? "OpenAI로 대체를 시도합니다." : describeProviderError(error)
          }`,
        );
        if (!hasOpenAI) throw error;
      }
    }

    const raw = await callOpenAI({
      apiKey: openAIApiKey,
      model: openAIModel,
      fallbackModel: openAIFallbackModel,
      system,
      prompt: revisionPrompt,
      maxOutputTokens: openAIRevisionMaxOutputTokens,
    });
    return {
      parsed: parseModelJson(raw, "openai", "수정안"),
      provider: "openai",
    };
  };

  onProgress?.("2/3 Gemini 수정안 생성");
  onLog?.("info", "2/3 Gemini 수정안 생성 요청");
  const revisionResult = await buildRevision();
  const geminiRevision = normalizeProposal(
    revisionResult.parsed,
    "gemini",
    datasetSummary.coupang_references,
    {
      datasetSummary,
      targetItemCount,
      maxItemCount,
      requirement,
      customerFacing,
    },
  );
  onLog?.(
    "success",
    `2/3 수정안 완료 (${geminiRevision.items.length}개 품목, ${
      revisionResult.provider === "gemini" ? "Gemini" : "OpenAI 대체"
    })`,
  );

  const finalPrompt = buildFinalConsensusPrompt(
    requirement,
    datasetSummary,
    chatgptDraft,
    geminiRevision,
    targetItemCount,
    maxItemCount,
    customerFacing,
  );
  const buildFinal = async (): Promise<{
    parsed: any;
    provider: "openai" | "gemini";
  }> => {
    if (hasOpenAI) {
      try {
        const raw = await callOpenAI({
          apiKey: openAIApiKey,
          model: openAIModel,
          fallbackModel: openAIFallbackModel,
          system,
          prompt: finalPrompt,
          maxOutputTokens: openAIFinalMaxOutputTokens,
        });
        return {
          parsed: parseModelJson(raw, "openai", "최종 합의안"),
          provider: "openai",
        };
      } catch (error) {
        const reason = describeProviderError(error);
        onLog?.(
          "warn",
          hasGemini
            ? `OpenAI 최종 합의안 생성 실패 (${reason}). Gemini로 대체합니다.`
            : `OpenAI 최종 합의안 생성 실패 (${reason})`,
        );
        if (!hasGemini) throw error;
      }
    }

    const raw = await callGemini({
      apiKey: geminiApiKey,
      model: geminiModel,
      fallbackModel: geminiFallbackModel,
      prompt: finalPrompt,
      maxOutputTokens: geminiFinalMaxOutputTokens,
      responseJsonSchema: buildGeminiFinalSchema(
        targetItemCount,
        maxItemCount,
      ),
    });
    return {
      parsed: parseModelJson(raw, "gemini", "최종 합의안"),
      provider: "gemini",
    };
  };

  onProgress?.("3/3 ChatGPT 최종 합의안 생성");
  onLog?.("info", "3/3 ChatGPT 최종 합의안 생성 요청");
  const finalResult = await buildFinal();

  const finalConsensus = normalizeFinalConsensus(
    finalResult.parsed,
    datasetSummary.coupang_references,
    {
      datasetSummary,
      targetItemCount,
      maxItemCount,
      requirement,
      customerFacing,
    },
  );
  if (finalConsensus.items.length === 0) {
    throw new Error("유효한 최종 큐레이션 결과가 없습니다.");
  }
  onLog?.(
    "success",
    `3/3 최종 합의 완료 (${finalConsensus.items.length}개 품목, 예상총액 ${money(finalConsensus.estimated_total_cost)}, ${
      finalResult.provider === "openai" ? "OpenAI" : "Gemini 대체"
    })`,
  );
  onProgress?.(null);

  return { chatgptDraft, geminiRevision, finalConsensus };
};
